import { Ref, useCallback, useImperativeHandle, useMemo } from "react";
import { ColorValue, StyleProp, ViewStyle } from "react-native";
import Animated, {
    useSharedValue,
    useAnimatedProps,
    withTiming,
    Easing,
    ReduceMotion,
} from "react-native-reanimated";
import { interpolatePath, parse } from "react-native-redash";
import Svg, { Defs, LinearGradient, Path, Stop } from "react-native-svg";

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AUTOMATIC_SLIDESHOW_TRANSITION = 1000;
type Point = { x: number; y: number };

type RandomLineProps = {
    width?: number;
    height?: number;
    points?: number;
    strokeWidth?: number;
    fixedPoints?: Point[]; // First 2 and last 2 points, total 4
    color: ColorValue | [ColorValue, ColorValue];
    style?: StyleProp<ViewStyle>;
};

const generateSmoothPath = (
    width: number,
    height: number,
    points: number,
    fixedPoints?: Point[],
) => {
    const step = width / (points - 1);
    const pts: Point[] = [];

    // Validate fixed points
    const useFixed = Array.isArray(fixedPoints) && fixedPoints.length >= 4;
    const [fp0, fp1, fpn2, fpn1] = useFixed ? fixedPoints : [null, null, null, null];

    for (let i = 0; i < points; i++) {
        if (useFixed) {
            if (i === 0 && fp0) {
                pts.push(fp0);
                continue;
            }
            if (i === 1 && fp1) {
                pts.push(fp1);
                continue;
            }
            if (i === points - 2 && fpn2) {
                pts.push(fpn2);
                continue;
            }
            if (i === points - 1 && fpn1) {
                pts.push(fpn1);
                continue;
            }
        }

        const xJitter = i > 0 ? Math.random() * step * 0.5 - step * 0.25 : 0;
        const x = i * step + xJitter;
        const y = Math.random() * height;
        pts.push({ x, y });
    }

    let d = `M${pts[0].x},${pts[0].y}`;

    for (let i = 1; i < pts.length - 1; i++) {
        const p1 = pts[i];
        const p2 = pts[i + 1];

        const cp2x = (p1.x + p2.x) / 2;
        const cp2y = (p1.y + p2.y) / 2;

        d += ` Q${p1.x},${p1.y} ${cp2x},${cp2y}`;
    }

    return d;
};

export type RandomLineFunctions = {
    randomize: () => void;
};

const PROGRESS_ANIMATION = {
    duration: AUTOMATIC_SLIDESHOW_TRANSITION,
    easing: Easing.out(Easing.ease),
    reduceMotion: ReduceMotion.System,
};

export const RandomLine = ({
    width = 320,
    height = 200,
    points = 11,
    strokeWidth = 11,
    fixedPoints,
    color,
    style,
    ref,
}: RandomLineProps & { ref?: Ref<RandomLineFunctions> }) => {
    const progress = useSharedValue(0);

    const randomize = useCallback(() => {
        progress.value = withTiming(progress.value + 1, PROGRESS_ANIMATION);
    }, [progress]);

    useImperativeHandle(ref, () => ({
        randomize,
    }));

    const paths = useMemo(
        () =>
            new Array(100)
                .fill(0)
                .map(() => parse(generateSmoothPath(width, height, points, fixedPoints))),
        [width, height, points, fixedPoints],
    );

    const animatedProps = useAnimatedProps(() => {
        return {
            d: interpolatePath(
                progress.value,
                paths.map((_, i) => i),
                paths,
            ),
            stroke: Array.isArray(color) ? `url(#grad)` : color,
            strokeWidth: strokeWidth,
            fill: "none",
        };
    });

    return (
        <Svg width={width} height={height} style={style}>
            {Array.isArray(color) ? (
                <>
                    <Defs>
                        <LinearGradient id="grad" x1="0" y1="0" x2="1" y2="0">
                            <Stop offset="0%" stopColor={color[0]} />
                            <Stop offset="100%" stopColor={color[1]} />
                        </LinearGradient>
                    </Defs>
                    <AnimatedPath animatedProps={animatedProps} />
                </>
            ) : (
                <AnimatedPath animatedProps={animatedProps} />
            )}
        </Svg>
    );
};
